/** Reporte masivo de órdenes pendientes para compartir por WhatsApp.
 *
 *  Formato exacto solicitado por operaciones:
 *  🔧 *ÓRDENES PENDIENTES EN TALLER*
 *  📅 Actualizado: [dd/mm/aaaa hh:mm]
 *
 *  📱 *SUCURSAL A*
 *  🔴 *12 días* | ORD-2026-XXXXXX
 *  iPhone 15 — Juan Pérez
 *  👨‍🔧 Fernando | En reparación
 *
 *  📊 *RESUMEN*
 *  Pendientes: N
 *  🔴 +10 días: X
 *  🟠 5–10 días: Y
 *  🟡 0–4 días: Z
 */

import { STATUS_LABEL } from "@/lib/constants";

// Semáforo (podría hacerse configurable a futuro).
const THRESHOLDS = { yellow_max: 4, orange_max: 10 };

export function ageEmoji(days) {
  if (days > THRESHOLDS.orange_max) return "🔴";
  if (days >= THRESHOLDS.yellow_max + 1) return "🟠"; // 5..10
  return "🟡"; // 0..4
}

function fmtDate(iso) {
  const d = iso ? new Date(iso) : new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
}

function equipmentLine(row) {
  const eq = [row.brand, row.model].filter(Boolean).join(" ").trim() || "Equipo";
  const client = row.customer_name || "—";
  return `${eq} — ${client}`;
}

function techLine(row) {
  const tech = row.technician_name || null;
  const status = STATUS_LABEL[row.status] || row.status;
  if (!tech) return `⚠️ Sin técnico asignado | ${status}`;
  return `👨‍🔧 ${tech} | ${status}`;
}

function daysLabel(days) {
  if (days <= 0) return "hoy";
  if (days === 1) return "1 día";
  return `${days} días`;
}

/** Construye el texto completo del reporte a partir del payload del backend. */
export function buildPendingOrdersReport(data) {
  const lines = [];
  lines.push("🔧 *ÓRDENES PENDIENTES EN TALLER*");
  lines.push(`📅 Actualizado: ${fmtDate(data?.generated_at)}`);
  lines.push("");

  const groups = data?.groups || [];
  if (!groups.length) {
    lines.push("Sin órdenes pendientes en este momento. ✅");
  }

  for (const g of groups) {
    const branch = (g.branch_name || "Sin sucursal").toUpperCase();
    lines.push(`📱 *${branch}*`);
    for (const it of (g.items || [])) {
      lines.push(`${ageEmoji(it.days)} *${daysLabel(it.days)}* | ${it.order_number}`);
      lines.push(equipmentLine(it));
      lines.push(techLine(it));
      lines.push("");
    }
  }

  // Resumen
  const allItems = groups.flatMap(g => g.items || []);
  const total = data?.total_pending ?? allItems.length;
  const red = allItems.filter(i => i.days > THRESHOLDS.orange_max).length;
  const orange = allItems.filter(i => i.days > THRESHOLDS.yellow_max && i.days <= THRESHOLDS.orange_max).length;
  const yellow = allItems.filter(i => i.days <= THRESHOLDS.yellow_max).length;

  // Resumen por estado REAL (solo estados con conteo >0, en el orden del flujo operativo)
  const STATUS_ORDER = [
    "RECEIVED", "IN_DIAGNOSIS", "WAITING_APPROVAL", "WAITING_PARTS",
    "IN_REPAIR", "TESTING", "IN_WARRANTY",
  ];
  const byStatus = {};
  for (const it of allItems) {
    byStatus[it.status] = (byStatus[it.status] || 0) + 1;
  }

  lines.push("📊 *RESUMEN*");
  lines.push(`Pendientes: ${total}`);
  // Por estado (omite estados con 0 para no alargar el WhatsApp)
  const statusLines = STATUS_ORDER
    .filter((s) => (byStatus[s] || 0) > 0)
    .map((s) => `• ${STATUS_LABEL[s] || s}: ${byStatus[s]}`);
  if (statusLines.length) {
    lines.push("Por estado:");
    lines.push(...statusLines);
  }
  // Antigüedad
  lines.push("Antigüedad:");
  if (red > 0) lines.push(`🔴 +${THRESHOLDS.orange_max} días: ${red}`);
  if (orange > 0) lines.push(`🟠 ${THRESHOLDS.yellow_max + 1}–${THRESHOLDS.orange_max} días: ${orange}`);
  if (yellow > 0) lines.push(`🟡 0–${THRESHOLDS.yellow_max} días: ${yellow}`);

  return lines.join("\n");
}
